import React, { useState, useEffect, useCallback } from 'react';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {instance} from "../../../API/API";

const ReportsDashboard = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('year'); // 'year' или 'month'

    // Цвета для графиков
    const COLORS = {
        currentYear: '#8884d8',
        previousYear: '#82ca9d',
        currentYearLight: '#9c9ae6',
        previousYearLight: '#a0d8b3'
    };

    // Получение данных
    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);
            const currentYear = new Date().getFullYear();
            const previousYear = currentYear - 1;

            // Запрос за текущий год
            const currentYearStart = Math.floor(new Date(currentYear, 0, 1).getTime() / 1000);
            const currentYearEnd = Math.floor(new Date(currentYear, 11, 31, 23, 59, 59).getTime() / 1000);

            // Запрос за предыдущий год
            const previousYearStart = Math.floor(new Date(previousYear, 0, 1).getTime() / 1000);
            const previousYearEnd = Math.floor(new Date(previousYear, 11, 31, 23, 59, 59).getTime() / 1000);

            const [currentYearResponse, previousYearResponse] = await Promise.all([
                instance.get(`reports/get_reports.php?start=${currentYearStart}&end=${currentYearEnd}`),
                instance.get(`reports/get_reports.php?start=${previousYearStart}&end=${previousYearEnd}`)
            ]);

            if (!currentYearResponse.ok || !previousYearResponse.ok) {
                throw new Error('Ошибка при загрузке данных');
            }

            const currentYearData = await currentYearResponse.json();
            const previousYearData = await previousYearResponse.json();

            // Добавляем метку года к каждому отчету
            const currentYearReports = currentYearData.reports.map(report => ({
                ...report,
                year: currentYear
            }));

            const previousYearReports = previousYearData.reports.map(report => ({
                ...report,
                year: previousYear
            }));

            setReports([...currentYearReports, ...previousYearReports]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    // Агрегация данных по месяцам
    const getMonthlyData = () => {
        const monthlyData = Array.from({ length: 12 }, (_, i) => ({
            month: new Date(0, i).toLocaleString('ru', { month: 'long' }),
            currentYear: 0,
            previousYear: 0,
            currentYearDuration: 0,
            previousYearDuration: 0
        }));

        reports.forEach(report => {
            const reportDate = new Date(report.time_download * 1000);
            const month = reportDate.getMonth();
            const year = reportDate.getFullYear();
            const currentYear = new Date().getFullYear();

            if (year === currentYear) {
                monthlyData[month].currentYear += 1;
                monthlyData[month].currentYearDuration += report.duration || 0;
            } else if (year === currentYear - 1) {
                monthlyData[month].previousYear += 1;
                monthlyData[month].previousYearDuration += report.duration || 0;
            }
        });

        return monthlyData;
    };

    // Агрегация данных по типам работ
    const getWorkTypeData = () => {
        const workTypes = {};

        reports.forEach(report => {
            const workType = report.work_type || 'Не указан';
            const year = new Date(report.time_download * 1000).getFullYear();
            const currentYear = new Date().getFullYear();

            if (!workTypes[workType]) {
                workTypes[workType] = { name: workType, currentYear: 0, previousYear: 0 };
            }

            if (year === currentYear) {
                workTypes[workType].currentYear += 1;
            } else if (year === currentYear - 1) {
                workTypes[workType].previousYear += 1;
            }
        });

        return Object.values(workTypes).slice(0, 10); // Топ 10 типов работ
    };

    // Общая статистика
    const getOverallStats = () => {
        const currentYear = new Date().getFullYear();
        const currentYearReports = reports.filter(r =>
            new Date(r.time_download * 1000).getFullYear() === currentYear
        );
        const previousYearReports = reports.filter(r =>
            new Date(r.time_download * 1000).getFullYear() === currentYear - 1
        );

        return {
            currentYear: {
                count: currentYearReports.length,
                duration: currentYearReports.reduce((sum, r) => sum + (r.duration || 0), 0),
                avgDuration: currentYearReports.length > 0
                    ? currentYearReports.reduce((sum, r) => sum + (r.duration || 0), 0) / currentYearReports.length
                    : 0
            },
            previousYear: {
                count: previousYearReports.length,
                duration: previousYearReports.reduce((sum, r) => sum + (r.duration || 0), 0),
                avgDuration: previousYearReports.length > 0
                    ? previousYearReports.reduce((sum, r) => sum + (r.duration || 0), 0) / previousYearReports.length
                    : 0
            }
        };
    };

    const monthlyData = getMonthlyData();
    const workTypeData = getWorkTypeData();
    const overallStats = getOverallStats();

    // Форматирование продолжительности
    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}ч ${minutes}м`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg">Загрузка данных...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-red-500 text-lg">Ошибка: {error}</div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
                Анализ отчетов сотрудников
            </h1>

            {/* Общая статистика */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Количество отчетов</h3>
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="text-2xl font-bold text-blue-600">
                                {overallStats.currentYear.count}
                            </div>
                            <div className="text-sm text-gray-500">Текущий год</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-green-600">
                                {overallStats.previousYear.count}
                            </div>
                            <div className="text-sm text-gray-500">Прошлый год</div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Общая продолжительность</h3>
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="text-2xl font-bold text-blue-600">
                                {formatDuration(overallStats.currentYear.duration)}
                            </div>
                            <div className="text-sm text-gray-500">Текущий год</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-green-600">
                                {formatDuration(overallStats.previousYear.duration)}
                            </div>
                            <div className="text-sm text-gray-500">Прошлый год</div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Средняя продолжительность</h3>
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="text-2xl font-bold text-blue-600">
                                {formatDuration(overallStats.currentYear.avgDuration)}
                            </div>
                            <div className="text-sm text-gray-500">Текущий год</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-green-600">
                                {formatDuration(overallStats.previousYear.avgDuration)}
                            </div>
                            <div className="text-sm text-gray-500">Прошлый год</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Графики */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* График количества отчетов по месяцам */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Количество отчетов по месяцам</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="currentYear"
                                stroke={COLORS.currentYear}
                                strokeWidth={2}
                                name="Текущий год"
                            />
                            <Line
                                type="monotone"
                                dataKey="previousYear"
                                stroke={COLORS.previousYear}
                                strokeWidth={2}
                                name="Прошлый год"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* График продолжительности работ по месяцам */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Продолжительность работ по месяцам (часы)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [`${Math.round(value / 3600)} ч`, 'Продолжительность']}
                            />
                            <Legend />
                            <Bar
                                dataKey="currentYearDuration"
                                fill={COLORS.currentYearLight}
                                name="Текущий год"
                            />
                            <Bar
                                dataKey="previousYearDuration"
                                fill={COLORS.previousYearLight}
                                name="Прошлый год"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Дополнительные графики */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Распределение по типам работ */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Распределение по типам работ</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={workTypeData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="name" width={80} />
                            <Tooltip />
                            <Legend />
                            <Bar
                                dataKey="currentYear"
                                fill={COLORS.currentYear}
                                name="Текущий год"
                            />
                            <Bar
                                dataKey="previousYear"
                                fill={COLORS.previousYear}
                                name="Прошлый год"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Сравнение годов */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Сравнение годов</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={[
                                {
                                    name: 'Количество отчетов',
                                    текущий: overallStats.currentYear.count,
                                    прошлый: overallStats.previousYear.count
                                },
                                {
                                    name: 'Общая продолжительность (ч)',
                                    текущий: Math.round(overallStats.currentYear.duration / 3600),
                                    прошлый: Math.round(overallStats.previousYear.duration / 3600)
                                }
                            ]}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="текущий" fill={COLORS.currentYear} />
                            <Bar dataKey="прошлый" fill={COLORS.previousYear} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Кнопка обновления */}
            <div className="mt-6 flex justify-center">
                <button
                    onClick={fetchReports}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
                >
                    Обновить данные
                </button>
            </div>
        </div>
    );
};

export default ReportsDashboard;